import axios from 'axios';
import notification from '../utils/notification';
import {
	HAS_STRIPE_ACCOUNT,
	SET_FILTERED_INVOICES,
	SET_ALL_INVOICES,
	SET_TOTAL_INVOICES,
	HAS_MORE_INVOICES,
	SET_INVOICES_NEXT_CURSOR,
	APPEND_INVOICES,
	APPEND_CUSTOMER_INVOICES,
	SET_CUSTOMER_INVOICES,
	SET_ALL_CUSTOMER_INVOICES,
	HAS_DETAILS_SUBMITTED,
	FETCH_USER,
} from './types';
import formatInvoices from '../components/utils/payments/formatInvoices';
import { formatDate } from '../components/utils/payments/formatDate';

const mergeUpdatedInvoiceData = (existingInvoice, updatedInvoice) => ({
	...existingInvoice,
	...updatedInvoice,
	// Keep local invoice linkage fields when Stripe response does not include them
	_invoice: updatedInvoice._invoice || existingInvoice._invoice,
	invoice: updatedInvoice.invoice || existingInvoice.invoice,
	_project: updatedInvoice._project ?? existingInvoice._project,
	type: updatedInvoice.type || existingInvoice.type,
});

const INVOICE_PAGE_SIZE = 10;

// Bumped whenever the payments table starts a new list (new filters, Pay/Works
// switch, leaving the page) so slow responses for an older list are dropped
// instead of overwriting the current one.
let invoiceListVersion = 0;
const startInvoiceList = () => ++invoiceListVersion;
const isCurrentInvoiceList = (version) => version === invoiceListVersion;

export const cancelInvoiceRequests = () => {
	startInvoiceList();
};

// The status-filtered search can return a short page (with a cursor) when it
// runs low on time. Keep requesting until the page is full so the table's
// fixed-size pages stay aligned.
const getInvoicePage = async (params, version) => {
	params.set('limit', INVOICE_PAGE_SIZE);
	let { data } = await axios.get(`/api/payments/invoices?${params}`);
	let invoices = data.invoices;

	while (
		isCurrentInvoiceList(version) &&
		params.has('status') &&
		invoices.length < INVOICE_PAGE_SIZE &&
		data.hasMore &&
		data.nextCursor
	) {
		params.set('startingAfter', data.nextCursor);
		params.set('limit', INVOICE_PAGE_SIZE - invoices.length);
		({ data } = await axios.get(`/api/payments/invoices?${params}`));
		invoices = invoices.concat(data.invoices);
	}

	if (invoices === data.invoices) {
		return data;
	}
	return { ...data, invoices, totalCount: invoices.length };
};

/**
 * PAYMENTS ACTIONS
 */

// check if a stripe account is connected to this business, return a bool
export const stripeAccountConnected = () => async (dispatch) => {
	try {
		const res = await axios.post('/api/payments/has-stripe-account');

		dispatch({
			type: HAS_STRIPE_ACCOUNT,
			payload: res.data.hasStripeAccount,
		});
		return { hasStripeAccount: res.data.hasStripeAccount };
	} catch {
		dispatch({
			type: HAS_STRIPE_ACCOUNT,
			payload: false,
		});

		return { hasStripeAccount: false };
	}
};

export const disconnectStripeAccount = () => async (dispatch) => {
	try {
		const res = await axios.post('/auth/stripe/disconnect');

		if (res.data && !res.data.error) {
			const hasStripeAccountRes = await axios.post(
				'/api/payments/has-stripe-account'
			);

			dispatch({
				type: HAS_STRIPE_ACCOUNT,
				payload: hasStripeAccountRes.data.hasStripeAccount,
			});

			const title = 'Success';
			const message = 'Disconnected your stripe account.';
			dispatch(notification(title, message, false));
		} else {
			dispatch(
				notification(
					'Error',
					'There was an error disconnecting your stripe account.',
					true
				)
			);
		}
	} catch {
		dispatch(
			notification(
				'Error',
				'There was an error disconnecting your stripe account.',
				true
			)
		);
	}
};

export const stripeAccountDetails = () => async (dispatch) => {
	const notifyError = () =>
		dispatch(
			notification(
				'Error',
				'Could not check your payments setup. Please refresh the page to try again.',
				true
			)
		);

	try {
		const res = await axios.get('/api/payments/has-details-submitted');

		if (res.data?.error) {
			notifyError();
			return;
		}

		dispatch({
			type: HAS_DETAILS_SUBMITTED,
			payload: Boolean(res.data?.hasDetailsSubmitted),
		});
	} catch {
		notifyError();
	}
};

export const updatePaymentTemplate =
	({ template, active, type }) =>
	async (dispatch) => {
		try {
			const res = await axios(`/api/payments/settings/update-template`, {
				method: 'post',
				data: { template, active, type },
			});

			if (res.data && res.data.error) {
				return notification(
					'Error',
					'There was an error updating your settings.',
					true
				);
			}

			return dispatch(
				notification(
					'Success',
					'Your automatic message has been updated.',
					false
				)
			);
		} catch {
			return notification(
				'Error',
				'There was an error updating your settings.',
				true
			);
		}
	};

export const handleIsIncludingConvenienceFeeByDefault =
	() => async (dispatch) => {
		try {
			const res = await axios.post(
				'/api/payments/settings/update-is-including-convenience-fee-by-default'
			);

			if (res.data && !res.data.error) {
				dispatch({ type: FETCH_USER, payload: res.data });
				const title = 'Success';
				const message = 'Updated your preferences.';
				dispatch(notification(title, message, false));
			} else {
				dispatch(
					notification(
						'Error',
						'There was an error updating your settings.',
						true
					)
				);
			}
		} catch (err) {
			console.error(
				'Error updating is including convenience fee by default:',
				err
			);
			dispatch(
				notification(
					'Error',
					'There was an error updating your settings.',
					true
				)
			);
		}
	};

// fetch first 10 invoices, now with optional date filters
export const fetchInvoices =
	({
		dateFrom,
		dateTo,
		amountMin,
		amountMax,
		referenceNumber,
		description,
		status,
	} = {}) =>
	async (dispatch) => {
		const version = startInvoiceList();
		try {
			const params = new URLSearchParams();
			if (dateFrom) params.append('dateFrom', dateFrom);
			if (dateTo) params.append('dateTo', dateTo);
			if (amountMin) params.append('amountMin', amountMin);
			if (amountMax) params.append('amountMax', amountMax);
			if (referenceNumber)
				params.append('referenceNumber', referenceNumber);
			if (description) params.append('description', description);
			if (status && Array.isArray(status) && status.length > 0)
				params.append('status', status.join(','));

			const data = await getInvoicePage(params, version);
			if (!isCurrentInvoiceList(version)) {
				return;
			}

			// Format invoices for consistent display
			const formattedInvoices = formatInvoices({
				invoices: data.invoices,
			});
			const { totalCount } = data;

			dispatch({
				type: SET_FILTERED_INVOICES,
				payload: formattedInvoices,
			});
			dispatch({
				type: SET_TOTAL_INVOICES,
				payload: totalCount,
			});
			dispatch({
				type: HAS_MORE_INVOICES,
				payload: data.hasMore,
			});
			// Opaque Stripe search cursor (present only on the status path)
			dispatch({
				type: SET_INVOICES_NEXT_CURSOR,
				payload: data.nextCursor || null,
			});
		} catch (error) {
			if (!isCurrentInvoiceList(version)) {
				return;
			}
			console.log('Error fetching invoices', error);
			dispatch({ type: SET_FILTERED_INVOICES, payload: [] });
			throw error;
		}
	};

// get 3 recent invoices
export const fetchRecentInvoices = () => async (dispatch) => {
	try {
		const res = await axios.get(`/api/payments/invoices?limit=3`);

		const formattedInvoices = formatInvoices({
			invoices: res.data.invoices,
		});

		dispatch({
			type: SET_FILTERED_INVOICES,
			payload: formattedInvoices,
		});
	} catch {
		console.log('Error fetching recent invoices');
		dispatch({ type: SET_FILTERED_INVOICES, payload: [] });
	}
};

export const fetchAllInvoices = () => async (dispatch) => {
	try {
		const res = await axios.get(`/api/payments/invoices-all`);
		const { totalCount } = res.data;

		const formattedInvoices = formatInvoices({
			invoices: res.data.invoices,
		});

		dispatch({
			type: SET_ALL_INVOICES,
			payload: formattedInvoices,
		});
		dispatch({
			type: SET_FILTERED_INVOICES,
			payload: formattedInvoices,
		});
		dispatch({
			type: SET_TOTAL_INVOICES,
			payload: totalCount,
		});
	} catch (error) {
		console.log('Error fetching all invoices', error);
		dispatch({ type: SET_FILTERED_INVOICES, payload: [] });
		// Don't throw error to prevent UI crashes, just log it
	}
};

// Fetch only runner invoices (Chekkit Works)
export const fetchRunnerInvoices =
	(filters = {}) =>
	async (dispatch) => {
		const version = startInvoiceList();
		try {
			const params = new URLSearchParams();
			if (filters.referenceNumber)
				params.append('referenceNumber', filters.referenceNumber);
			if (filters.description)
				params.append('description', filters.description);
			if (
				filters.status &&
				Array.isArray(filters.status) &&
				filters.status.length > 0
			) {
				params.append('status', filters.status.join(','));
			}
			if (filters.amountMin)
				params.append('amountMin', filters.amountMin);
			if (filters.amountMax)
				params.append('amountMax', filters.amountMax);
			if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
			if (filters.dateTo) params.append('dateTo', filters.dateTo);

			const apiUrl = `/api/payments/runner-invoices${
				params.toString() ? `?${params.toString()}` : ''
			}`;
			const res = await axios.get(apiUrl);
			if (!isCurrentInvoiceList(version)) {
				return;
			}

			const formattedInvoices = formatInvoices({
				invoices: res.data.invoices,
			});

			dispatch({
				type: SET_FILTERED_INVOICES,
				payload: formattedInvoices,
			});
			dispatch({
				type: SET_TOTAL_INVOICES,
				payload: res.data.totalCount,
			});
			dispatch({
				type: HAS_MORE_INVOICES,
				payload: res.data.hasMore,
			});
		} catch (error) {
			if (!isCurrentInvoiceList(version)) {
				return;
			}
			console.log('Error fetching runner invoices', error);
			dispatch({ type: SET_FILTERED_INVOICES, payload: [] });
			throw error;
		}
	};

export const getNextRunnerInvoices =
	(startingAfter, filters = {}) =>
	async (dispatch) => {
		const version = invoiceListVersion;
		try {
			const params = new URLSearchParams();
			params.append('startingAfter', startingAfter);
			if (filters.referenceNumber)
				params.append('referenceNumber', filters.referenceNumber);
			if (filters.description)
				params.append('description', filters.description);
			if (
				filters.status &&
				Array.isArray(filters.status) &&
				filters.status.length > 0
			) {
				params.append('status', filters.status.join(','));
			}
			if (filters.amountMin)
				params.append('amountMin', filters.amountMin);
			if (filters.amountMax)
				params.append('amountMax', filters.amountMax);
			if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
			if (filters.dateTo) params.append('dateTo', filters.dateTo);

			const apiUrl = `/api/payments/runner-invoices?${params.toString()}`;
			const res = await axios.get(apiUrl);
			if (!isCurrentInvoiceList(version)) {
				return;
			}

			const formattedInvoices = formatInvoices({
				invoices: res.data.invoices,
			});

			dispatch({
				type: APPEND_INVOICES,
				payload: formattedInvoices,
			});

			dispatch({
				type: HAS_MORE_INVOICES,
				payload: res.data.hasMore,
			});
		} catch (error) {
			console.log('Error fetching next runner invoices', error);
		}
	};

export const fetchAllCustomerInvoices = (_customer) => async (dispatch) => {
	try {
		const res = await axios.get(
			`/api/payments/customer-invoices-all?_customer=${_customer}`
		);

		const customerInvoices = formatInvoices({
			invoices: res.data.customerInvoices,
		});

		dispatch({
			type: SET_ALL_CUSTOMER_INVOICES,
			payload: customerInvoices,
		});
	} catch {
		console.log('Error fetching all customer invoices');
		dispatch({ type: SET_FILTERED_INVOICES, payload: [] });
	}
};

// staringAfter is the ID of the latest returned invoice
// get the next 10 after the startingAfter
export const getNextInvoices =
	(startingAfter, filters = {}) =>
	async (dispatch) => {
		const version = invoiceListVersion;
		try {
			const params = new URLSearchParams();
			params.append('startingAfter', startingAfter);
			if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
			if (filters.dateTo) params.append('dateTo', filters.dateTo);
			if (filters.amountMin)
				params.append('amountMin', filters.amountMin);
			if (filters.amountMax)
				params.append('amountMax', filters.amountMax);
			if (filters.referenceNumber)
				params.append('referenceNumber', filters.referenceNumber);
			if (filters.description)
				params.append('description', filters.description);
			if (
				filters.status &&
				Array.isArray(filters.status) &&
				filters.status.length > 0
			) {
				params.append('status', filters.status.join(','));
			}

			const data = await getInvoicePage(params, version);
			if (!isCurrentInvoiceList(version)) {
				return;
			}

			const formattedInvoices = formatInvoices({
				invoices: data.invoices,
			});

			dispatch({
				type: APPEND_INVOICES,
				payload: formattedInvoices,
			});

			dispatch({
				type: HAS_MORE_INVOICES,
				payload: data.hasMore,
			});
			// Advance the opaque Stripe search cursor for the next page
			dispatch({
				type: SET_INVOICES_NEXT_CURSOR,
				payload: data.nextCursor || null,
			});
		} catch {
			console.log('error appending invoices');
		}
	};

export const updateSingleInvoice =
	(existingInvoices, updatedInvoice) => async (dispatch) => {
		try {
			// format the new updatedInvoice to be proper.
			const formattedUpdatedInvoice = formatInvoices({
				invoices: [updatedInvoice],
			});

			// find the invoice to update and update it with the new formatted one
			const newInvoices = existingInvoices.map((invoice) => {
				if (invoice.id === formattedUpdatedInvoice[0].id) {
					return mergeUpdatedInvoiceData(
						invoice,
						formattedUpdatedInvoice[0]
					);
				}

				return invoice;
			});

			dispatch({
				type: SET_FILTERED_INVOICES,
				payload: newInvoices,
			});
		} catch {
			console.log('Error updating invoice');
			dispatch({ type: SET_FILTERED_INVOICES, payload: [] });
		}
	};

// updated invoices is not formatted, whereas existingInvoices is formatted.
// update 1 invoice(updatedInvoice) from an array of existingInvoices
export const updateSingleCustomerInvoice =
	(existingInvoices, updatedInvoice) => async (dispatch) => {
		try {
			// format the new updatedInvoice to be proper.
			const formattedUpdatedInvoice = formatInvoices({
				invoices: [updatedInvoice],
			});
			// find the invoice to update and update it with the new formatted one
			const newInvoices = existingInvoices.map((invoice) => {
				if (invoice.id === formattedUpdatedInvoice[0].id) {
					return mergeUpdatedInvoiceData(
						invoice,
						formattedUpdatedInvoice[0]
					);
				}

				return invoice;
			});

			dispatch({
				type: SET_CUSTOMER_INVOICES,
				payload: newInvoices,
			});
		} catch {
			console.log('Error updating customer invoice');
			dispatch({ type: SET_FILTERED_INVOICES, payload: [] });
		}
	};

export const fetchCustomerInvoices =
	(_customer, invoiceType = 'pay', limit = 10, filters = {}) =>
	async (dispatch) => {
		try {
			if (_customer === 'undefined') {
				dispatch({ type: SET_CUSTOMER_INVOICES, payload: [] });
				return;
			}

			// Build the API URL with proper parameters
			const params = new URLSearchParams({
				_customer,
				type: invoiceType,
				limit: limit.toString(),
			});

			// Add filter parameters if provided
			if (filters.amountMin)
				params.append('amountMin', filters.amountMin);
			if (filters.amountMax)
				params.append('amountMax', filters.amountMax);
			if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
			if (filters.dateTo) params.append('dateTo', filters.dateTo);
			if (filters.referenceNumber)
				params.append('referenceNumber', filters.referenceNumber);
			if (filters.description)
				params.append('description', filters.description);
			if (
				filters.status &&
				Array.isArray(filters.status) &&
				filters.status.length > 0
			) {
				params.append('status', filters.status.join(','));
			}

			const res = await axios.get(
				`/api/payments/customer-invoices?${params.toString()}`
			);
			const { totalCount } = res.data;

			const formattedCustomerInvoices = formatInvoices({
				invoices: res.data.customerInvoices,
			});

			dispatch({
				type: SET_CUSTOMER_INVOICES,
				payload: formattedCustomerInvoices,
			});
			dispatch({
				type: SET_TOTAL_INVOICES,
				payload: totalCount,
			});

			dispatch({
				type: HAS_MORE_INVOICES,
				payload: res.data.hasMore,
			});
		} catch {
			console.log('Error fetching customer invoices');
			dispatch({ type: SET_CUSTOMER_INVOICES, payload: [] });
		}
	};

// Fetch recent invoices for a customer (mixed types, optimized for inbox/webchat)
export const fetchCustomerRecentInvoices = (_customer) => async (dispatch) => {
	try {
		if (_customer === 'undefined') {
			dispatch({ type: SET_CUSTOMER_INVOICES, payload: [] });
			return;
		}

		const res = await axios.get(
			`/api/payments/customer-recent-invoices?_customer=${_customer}`
		);

		const formattedCustomerInvoices = formatInvoices({
			invoices: res.data.customerInvoices,
		});

		dispatch({
			type: SET_CUSTOMER_INVOICES,
			payload: formattedCustomerInvoices,
		});
		dispatch({
			type: SET_TOTAL_INVOICES,
			payload: res.data.totalCustomerCount,
		});
	} catch {
		console.log('Error fetching customer recent invoices');
		dispatch({ type: SET_CUSTOMER_INVOICES, payload: [] });
	}
};

// staringAfter is the ID of the latest returned invoice
// get the next 10 customer after the startingAfter
export const getNextCustomerInvoices =
	({ _customer, startingAfter, invoiceType = 'pay', filters = {} }) =>
	async (dispatch) => {
		try {
			// Build the API URL with proper parameters
			const params = new URLSearchParams({
				_customer,
				startingAfter,
				type: invoiceType,
			});

			// Add filter parameters if provided
			if (filters.amountMin)
				params.append('amountMin', filters.amountMin);
			if (filters.amountMax)
				params.append('amountMax', filters.amountMax);
			if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
			if (filters.dateTo) params.append('dateTo', filters.dateTo);
			if (filters.referenceNumber)
				params.append('referenceNumber', filters.referenceNumber);
			if (filters.description)
				params.append('description', filters.description);
			if (
				filters.status &&
				Array.isArray(filters.status) &&
				filters.status.length > 0
			) {
				params.append('status', filters.status.join(','));
			}

			const res = await axios.get(
				`/api/payments/customer-invoices?${params.toString()}`
			);

			const formattedCustomerInvoices = formatInvoices({
				invoices: res.data.customerInvoices,
			});

			dispatch({
				type: APPEND_CUSTOMER_INVOICES,
				payload: formattedCustomerInvoices,
			});

			dispatch({
				type: HAS_MORE_INVOICES,
				payload: res.data.hasMore,
			});
		} catch {
			console.log('Error fetching next customer invoices');
		}
	};

export const setInvoices = (invoices, totalCount) => async (dispatch) => {
	startInvoiceList();
	try {
		dispatch({
			type: SET_FILTERED_INVOICES,
			payload: invoices,
		});
		dispatch({
			type: SET_TOTAL_INVOICES,
			payload: totalCount,
		});
	} catch {
		console.log('error appending invoices');
	}
};

export const setCustomerInvoices =
	(invoices, totalCount) => async (dispatch) => {
		try {
			dispatch({
				type: SET_CUSTOMER_INVOICES,
				payload: invoices,
			});
			dispatch({
				type: SET_TOTAL_INVOICES,
				payload: totalCount,
			});
		} catch {
			console.log('error appending invoices');
		}
	};

export const setAllInvoices = (invoices) => async (dispatch) => {
	try {
		dispatch({
			type: SET_ALL_INVOICES,
			payload: invoices,
		});
	} catch {
		console.log('error appending invoices');
	}
};

export const setAllCustomerInvoices = (invoices) => async (dispatch) => {
	try {
		dispatch({
			type: SET_ALL_CUSTOMER_INVOICES,
			payload: invoices,
		});
	} catch {
		console.log('error appending invoices');
	}
};

// Export invoices to CSV
export const exportInvoices = (invoices) => {
	try {
		// Define CSV headers
		const headers = [
			'Date',
			'Date Paid',
			'Amount',
			'Customer',
			'Phone/Email',
			'Reference',
			'Status',
			'Type',
			'Description',
			'Currency',
		];

		// Convert invoices to CSV rows
		const csvRows = invoices.map((invoice) => {
			const customer = invoice.customer || {};
			const type = invoice.type === 'runner_invoice' ? 'works' : 'pay';
			const referenceNumber =
				invoice.number ||
				invoice.referenceNumber ||
				(invoice.metadata && invoice.metadata.reference_number) ||
				(invoice.invoice && invoice.invoice.referenceNumber) ||
				'';
			const phoneOrEmail =
				customer.phone ||
				customer.phoneFormatted ||
				customer.email ||
				'';
			const dateForExport = formatDate({
				date: invoice.created,
				platform:
					invoice.type === 'runner_invoice' ? 'runner' : 'stripe',
				alwaysIncludeYear: true,
			});
			const paidDateForExport = invoice.paidAt
				? formatDate({
						date: invoice.paidAt,
						platform: 'runner',
						alwaysIncludeYear: true,
				  })
				: '';
			// Amounts are stored in cents (minor units) for both pay and works
			// invoices; export dollars so the CSV matches the on-screen values.
			const amountInCents = Number(
				invoice.amount ?? invoice.total ?? 0
			);
			const amountForExport = (
				(Number.isFinite(amountInCents) ? amountInCents : 0) / 100
			).toFixed(2);
			return [
				dateForExport, // Date
				paidDateForExport, // Date Paid
				amountForExport, // Amount
				customer.name || '', // Customer
				phoneOrEmail, // Phone/Email
				referenceNumber, // Reference
				invoice.statusFormatted || invoice.status || '', // Status
				type, // Type
				invoice.description || invoice.name || '', // Description
				invoice.currency || 'usd', // Currency
			];
		});

		// Combine headers and rows
		const csvContent = [headers, ...csvRows]
			.map((row) => row.map((cell) => `"${cell}"`).join(','))
			.join('\n');

		// Create and download CSV file
		const blob = new Blob([csvContent], {
			type: 'text/csv;charset=utf-8;',
		});
		const link = document.createElement('a');
		const url = URL.createObjectURL(blob);
		link.setAttribute('href', url);
		link.setAttribute(
			'download',
			`invoices_${new Date().toISOString().split('T')[0]}.csv`
		);
		link.style.visibility = 'hidden';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	} catch (error) {
		console.log('Error exporting invoices', error);
		throw error;
	}
};
